import { useEffect, useRef, useState } from 'react'

import { EMPTY_LIST, EMPTY_OBJECT } from '../../../../constants/common'
import { useNotification, useRequestQueue } from '../../../../hooks'
import { getItemsIds } from '../../../../utils/common'
import {
  isNewsboardPostFileDeleteConfirmationDisabled,
  NewsboardPostFileDeleteConfirmationModal,
} from '../../NewsboardPostFileDeleteConfirmationModal'

import {
  getCanEditNewsboardPostFiles,
  getNewsboardPostFiles,
  getRemainingUploadingFiles,
} from './useNewsboardPostFilesEdit.utils'

export function useNewsboardPostFilesEdit({
  data,
  onCloseModal,
  onSetModal,
  onUpdateFiles,
}) {
  const { handleAddErrorNotification } = useNotification()
  const { enqueue } = useRequestQueue()

  const [uploadingFiles, setUploadingFiles] = useState(EMPTY_LIST)

  const files = getNewsboardPostFiles(data)
  const canEditFiles = getCanEditNewsboardPostFiles(data)

  // SET semantics: a save builds on the list the previous save sent, not on `data`
  const nextFilesRef = useRef(files)
  const pendingSavesRef = useRef(0)

  // Only an idle queue lets `data` re-seed the baseline queued saves build on
  useEffect(() => {
    if (pendingSavesRef.current === 0) {
      nextFilesRef.current = files
    }
  }, [files])

  // Resolves to whether the save landed; `onUpdateFiles` owns the error toast
  async function handleSaveFiles(getNextFiles) {
    pendingSavesRef.current += 1

    return enqueue(async () => {
      const prevFiles = nextFilesRef.current
      const nextFiles = getNextFiles(prevFiles)

      nextFilesRef.current = nextFiles

      try {
        await onUpdateFiles(getItemsIds(nextFiles))

        return true
      } catch {
        nextFilesRef.current = prevFiles

        return false
      } finally {
        pendingSavesRef.current -= 1
      }
    })
  }

  async function handleDeleteFile(file) {
    return handleSaveFiles(prevFiles => prevFiles.filter(f => f.id !== file.id))
  }

  function handleRemoveFile(file) {
    if (isNewsboardPostFileDeleteConfirmationDisabled()) {
      handleDeleteFile(file)

      return
    }

    async function handleSubmitDelete() {
      if (await handleDeleteFile(file)) {
        onCloseModal()
      }
    }

    onSetModal(
      <NewsboardPostFileDeleteConfirmationModal
        onCancel={onCloseModal}
        onSubmit={handleSubmitDelete}
      />
    )
  }

  function handleStartUpload(filesList) {
    setUploadingFiles(prev => [...prev, ...filesList])
  }

  function handleFinishUpload(acceptedFiles, errorFiles = EMPTY_LIST) {
    setUploadingFiles(prev =>
      getRemainingUploadingFiles(prev, [...acceptedFiles, ...errorFiles])
    )

    if (acceptedFiles.length > 0) {
      handleSaveFiles(prevFiles => [...prevFiles, ...acceptedFiles])
    }
  }

  function handleUploadErrors(errorFiles) {
    errorFiles.forEach(file => {
      handleAddErrorNotification(
        file.error?.text || 'Error.UploadingFileApiErrorKey'
      )
    })
  }

  return {
    files,
    galleryEditProps: canEditFiles
      ? {
          uploadingFiles,
          uploadProps: {
            uploadedFilesLength: files.length,
            onFinishUpload: handleFinishUpload,
            onStartUpload: handleStartUpload,
            onUploadErrors: handleUploadErrors,
          },
          onRemoveFile: handleRemoveFile,
        }
      : EMPTY_OBJECT,
    isGalleryVisible: files.length > 0 || canEditFiles,
  }
}
